import React, { useState, useEffect, useCallback } from 'react';
import { Send, Mail, ShieldCheck, AlertTriangle, Loader2, Users, Key } from 'lucide-react';
import apiService from '../../services/api';
import './invitation-sender.scss';

interface Preview {
  subject: string;
  intro: string;
  cta_label: string;
  recipients_count: number;
  by_role?: Record<string, number>;
  test_redirect: string | null;
  login_url: string;
  pending_count?: number;
  pending_by_role?: Record<string, number>;
  pending?: { name: string; email: string; role: string; last_login: string | null }[];
}

const roleSummary = (byRole?: Record<string, number>) =>
  byRole ? Object.entries(byRole).map(([r, n]) => `${n} ${r}`).join(' · ') : '';

const InvitationSender: React.FC = () => {
  const [p, setP] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [sendingPending, setSendingPending] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [fb, setFb] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.invitations.preview();
      setP(res.data?.data || null);
    } catch {
      setFb({ type: 'err', text: 'No se pudo cargar la vista previa.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setSendingTest(true); setFb(null);
    try {
      const res = await apiService.invitations.sendTest();
      const to = res.data?.data?.to;
      setFb({ type: res.data?.success ? 'ok' : 'err', text: res.data?.success ? `Invitación de prueba enviada a ${to}` : 'No se pudo enviar la prueba.' });
    } catch {
      setFb({ type: 'err', text: 'Error al enviar la prueba.' });
    } finally {
      setSendingTest(false);
    }
  };

  const send = async (onlyPending: boolean) => {
    const n = onlyPending ? (p?.pending_count ?? 0) : p?.recipients_count;
    const who = onlyPending
      ? `${n} personas que todavía no cambiaron su contraseña (a quienes ya la cambiaron no se les envía nada)`
      : `${n} usuarios del programa`;
    const redirected = p?.test_redirect;
    const msg = redirected
      ? `Se enviará la invitación a ${who}.\n\nMODO PRUEBA activo: todos los correos se redirigen a ${redirected} (los usuarios reales NO reciben nada).\n\n¿Continuar?`
      : `⚠️ ATENCIÓN: esto enviará la invitación REAL a ${who}.\n\nNo hay modo prueba activo — los correos llegarán a los destinatarios reales.\n\n¿Continuar?`;
    if (!window.confirm(msg)) return;

    const setBusy = onlyPending ? setSendingPending : setSendingAll;
    setBusy(true); setFb(null);
    try {
      const res = await apiService.invitations.sendAll(onlyPending);
      const d = res.data?.data || {};
      const failed: string[] = d.failed || [];
      setFb({
        type: failed.length ? 'err' : 'ok',
        text: `Invitación enviada a ${d.sent} de ${d.total} destinatario(s).` + (failed.length ? ` Fallaron: ${failed.join(', ')}` : ''),
      });
    } catch {
      setFb({ type: 'err', text: 'Error al ejecutar el envío masivo.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inv-sender">
      <div className="inv-head">
        <div className="inv-eyebrow"><Send size={14} /> ENVÍO MASIVO</div>
        <h2 className="inv-title">Invitación al <em>sistema</em></h2>
        <p className="inv-meta">
          Envía la invitación de presentación del programa a los participantes. El texto se edita en la sección de notificaciones (plantilla «Bienvenida»).
        </p>
      </div>

      {loading ? (
        <div className="inv-loading"><Loader2 className="spin" size={20} /> Cargando…</div>
      ) : p ? (
        <>
          {/* Seguridad */}
          {p.test_redirect ? (
            <div className="inv-safe">
              <ShieldCheck size={18} />
              <div>
                <strong>Modo prueba activo.</strong> Todos los correos se redirigen a <b>{p.test_redirect}</b>. Los usuarios reales no reciben nada.
              </div>
            </div>
          ) : (
            <div className="inv-warn">
              <AlertTriangle size={18} />
              <div><strong>Envío real.</strong> No hay modo prueba: los correos llegarán a los destinatarios reales.</div>
            </div>
          )}

          {/* Preview del correo */}
          <div className="inv-preview">
            <div className="inv-preview-subject"><Mail size={15} /> {p.subject}</div>
            <div className="inv-preview-body"><b>Hola [nombre],</b><br /><br />{p.intro}</div>
            <div className="inv-preview-creds">
              <span className="inv-preview-creds-label">Tus datos de acceso</span>
              Correo: <b>[su correo]</b> · Contraseña: <b>[su contraseña]</b>
            </div>
            <span className="inv-preview-cta">{p.cta_label}</span>
            <span className="inv-preview-link">Ver las instrucciones de uso →</span>
          </div>

          {/* Destinatarios */}
          <div className="inv-recipients">
            <Users size={16} /> <b>{p.recipients_count}</b> destinatarios personalizados
            {p.by_role && <span className="inv-recipients-roles"> ({roleSummary(p.by_role)})</span>}
          </div>
          <div className="inv-recipients">
            <Key size={16} /> <b>{p.pending_count ?? 0}</b> todavía no cambiaron su contraseña
            {p.pending_by_role && (p.pending_count ?? 0) > 0 && (
              <span className="inv-recipients-roles"> ({roleSummary(p.pending_by_role)})</span>
            )}
            {(p.pending_count ?? 0) > 0 && (
              <button type="button" className="inv-link-btn" onClick={() => setShowPending(v => !v)}>
                {showPending ? 'Ocultar lista' : 'Ver quiénes'}
              </button>
            )}
          </div>
          {showPending && p.pending && (
            <ul className="inv-pending-list">
              {p.pending.map(x => (
                <li key={x.email}>
                  <b>{x.name || x.email}</b> · {x.role} · {x.email}
                  <span> · {x.last_login ? `último ingreso ${x.last_login.slice(0, 10)}` : 'nunca entró'}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="inv-note-personal">Cada persona recibe un correo individual con <b>su</b> correo y <b>su</b> contraseña. No se usan copias ni CC.</p>

          {fb && <div className={`inv-fb ${fb.type}`}>{fb.text}</div>}

          {/* Acciones */}
          <div className="inv-actions">
            <button className="inv-btn ghost" onClick={sendTest} disabled={sendingTest}>
              {sendingTest ? <Loader2 size={15} className="spin" /> : <Mail size={15} />} Enviar prueba
            </button>
            <button className="inv-btn ghost" onClick={() => send(false)} disabled={sendingAll || sendingPending || p.recipients_count === 0}>
              {sendingAll ? <Loader2 size={15} className="spin" /> : <Send size={15} />} Enviar a todos ({p.recipients_count})
            </button>
            <button className="inv-btn primary" onClick={() => send(true)} disabled={sendingAll || sendingPending || !p.pending_count}>
              {sendingPending ? <Loader2 size={15} className="spin" /> : <Send size={15} />} Reenviar a quienes no cambiaron su contraseña ({p.pending_count ?? 0})
            </button>
          </div>
        </>
      ) : (
        <div className="inv-fb err">No se pudo cargar la vista previa.</div>
      )}
    </div>
  );
};

export default InvitationSender;
